import React, { useState, useEffect } from "react";
import {
  Check,
  Minus,
  ChevronDown,
  ChevronUp,
  Info,
  CheckSquare,
  Square,
} from "lucide-react";

interface AddOnsConsoleProps {
  organizationId?: string;
  selectedIds?: string[];
  onToggle?: (id: string) => void;
}

import { offeringsService } from "../../../../services/api/settings";
import { OfferingAddon } from "../../../../store/types/settings";
import formatText from "../../../../utility/formatText";
import Skeleton from "../../../../components/elements/Skeleton";

const AddOnsConsole: React.FC<AddOnsConsoleProps> = ({
  organizationId,
  selectedIds = [],
  onToggle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addOns, setAddOns] = useState<any[]>([]);

  useEffect(() => {
    const fetchAddOns = async () => {
      setLoading(true);
      try {
        const response = await offeringsService.getAll();
        if (response.status === "success" && response.data?.CONSOLE?.addons) {
          // Map API response to Component format
          const mappedAddOns = response.data.CONSOLE.addons
            .filter((a) => a.category === "INTEGRATION")
            .map((item: OfferingAddon) => ({
              id: item.id,
              name: item.name,
              price:
                typeof item.price === "string"
                  ? parseFloat(item.price)
                  : item.price,
              billingType: item.billingType,
              activation: "Immediate",
              essentialOperations: true,
              businessGrowth: true,
              enterpriseSuit: true,
            }));
          setAddOns(mappedAddOns);
        }
      } catch (error) {
        console.error("Failed to fetch console add-ons", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAddOns();
  }, [organizationId]);

  const renderStatus = (status: boolean) => {
    return status ? (
      <Check className="w-5 h-5 text-gray-800 mx-auto" />
    ) : (
      <Minus className="w-5 h-5 text-gray-400 mx-auto" />
    );
  };

  const formatPrice = (price: number | string) => {
    if (typeof price === "string") return price;
    return new Intl.NumberFormat("en-NG", {
      style: "decimal",
      // currency: "NGN",
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <section id="direct-addons" className="pb-6 px-3 lg:px-8">
      <div className="max-w-[900px] mx-auto">
        <div className="bg-white rounded-[15px] shadow-sm border border-primary overflow-hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors rounded-[15px]"
          >
            <h3 className="text-[15px] md:text-[17px] font-medium text-[#212121]">
              Add-Ons
            </h3>
            {isOpen ? (
              <ChevronUp className="w-6 h-6 text-gray-500" />
            ) : (
              <ChevronDown className="w-6 h-6 text-gray-500" />
            )}
          </button>

          {isOpen && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-3 w-[50px] text-center">
                      {onToggle && (
                        <div
                          className="flex items-center justify-center cursor-pointer"
                          onClick={() => {
                            const allIds = addOns.map((f) => f.id);
                            const selectableConsole: any[] = [];
                            const allSelected = allIds.every((id) =>
                              selectedIds.includes(id),
                            );
                            if (allSelected) {
                              // Deselect all
                              allIds.forEach((id) => {
                                if (selectedIds.includes(id)) onToggle(id);
                              });
                            } else {
                              // Select all
                              allIds.forEach((id) => {
                                if (!selectedIds.includes(id)) onToggle(id);
                              });
                            }
                          }}
                        >
                          {addOns.length > 0 &&
                          addOns.every((f) => selectedIds.includes(f.id)) ? (
                            <CheckSquare className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                          )}
                        </div>
                      )}
                    </th>
                    <th className="text-left px-6 py-3 text-[14px] font-medium text-[#212121] w-[35%]">
                      Services
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[17.5%]">
                      Essential Operations
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[17.5%]">
                      Business Growth
                    </th>
                    <th className="text-center px-6 py-3 text-[14px] font-medium text-[#212121] w-[17.5%]">
                      Enterprise Suite
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          <td className="px-3 py-4 text-center">
                            <Skeleton
                              variant="rectangular"
                              height={20}
                              width={20}
                              className="mx-auto rounded"
                            />
                          </td>
                          <td className="px-3 py-4">
                            <Skeleton variant="text" height={16} width="80%" />
                          </td>
                          <td className="px-6 py-4">
                            <Skeleton
                              variant="circular"
                              height={20}
                              width={20}
                              className="mx-auto"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <Skeleton
                              variant="circular"
                              height={20}
                              width={20}
                              className="mx-auto"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <Skeleton
                              variant="circular"
                              height={20}
                              width={20}
                              className="mx-auto"
                            />
                          </td>
                        </tr>
                      ))
                    : addOns.map((feature, index) => {
                        const isSelected = selectedIds.includes(feature.id);
                        return (
                          <tr
                            key={feature.id}
                            className={`hover:bg-gray-50 transition-colors ${
                              isSelected ? "bg-blue-50/50" : ""
                            }`}
                            onClick={() => onToggle && onToggle(feature.id)}
                          >
                            <td className="px-3 py-3 text-center cursor-pointer">
                              {onToggle && (
                                <div className="flex items-center justify-center">
                                  {isSelected ? (
                                    <CheckSquare className="w-5 h-5 text-blue-600" />
                                  ) : (
                                    <Square className="w-5 h-5 text-gray-300 hover:text-gray-400" />
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-3 text-[14px] text-[#616161]">
                              <div className="flex items-center gap-1 group relative">
                                {feature.name}
                                {feature && (
                                  <>
                                    <Info className="w-4 h-4 text-gray-400 cursor-help" />
                                    <div className="invisible group-hover:visible absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 bg-white shadow-xl border border-gray-100 rounded-lg p-3 w-max min-w-[200px]">
                                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                        <div className="text-gray-500 text-xs">
                                          Price
                                        </div>
                                        <div className="text-gray-900 font-normal text-right">
                                          {formatPrice(feature.price)}
                                        </div>
                                        <div className="text-gray-500 text-xs">
                                          Billing Type
                                        </div>
                                        <div className="text-gray-900 font-normal text-right">
                                          {formatText(feature.billingType)}
                                        </div>
                                        <div className="text-gray-500 text-xs">
                                          Activation
                                        </div>
                                        <div className="text-gray-900 font-normal text-right">
                                          {feature.activation}
                                        </div>
                                      </div>
                                      <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 border-8 border-transparent border-r-white"></div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-3 text-center">
                              {renderStatus(feature.essentialOperations)}
                            </td>
                            <td className="px-6 py-3 text-center">
                              {renderStatus(feature.businessGrowth)}
                            </td>
                            <td className="px-6 py-3 text-center">
                              {renderStatus(feature.enterpriseSuit)}
                            </td>
                          </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AddOnsConsole;
